"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Hash, Search } from "lucide-react";
import { motion } from "motion/react";
import { useMemo, useState } from "react";

interface GlossaryEntry {
  term: string;
  acronym?: string;
  definition: string;
  category: string;
  relatedTerms?: string[];
}

export default function Glossary() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const glossaryEntries: GlossaryEntry[] = [
    // A
    {
      term: "Attrition Clause",
      definition:
        "A contract clause that outlines the penalties if the event or group uses fewer rooms or services than originally contracted for. This protects venues from lost revenue when bookings don't meet projections.",
      category: "Contracts & Legal",
      relatedTerms: ["Cancellation Clause", "Force Majeure", "Resell Clause"],
    },
    {
      term: "Audio Visual",
      acronym: "AV",
      definition:
        "Equipment and services related to sound, lighting, and visual presentation including microphones, projectors, screens, speakers, and technical support staff.",
      category: "Technology & Equipment",
    },
    {
      term: "Accommodation",
      definition:
        "Lodging arrangements for event attendees, including hotels, resorts, or alternative lodging options. Often involves negotiating group rates and room blocks.",
      category: "Venue & Accommodation",
    },

    // B
    {
      term: "Banquet Event Order",
      acronym: "BEO",
      definition:
        "A detailed document outlining all requirements for an event, including food and beverage specifications, room setup, timing, guest count, and audio-visual needs. This serves as the final contract between planner and venue.",
      category: "Event Planning",
      relatedTerms: ["Function Sheet", "Event Timeline", "Setup Diagram"],
    },
    {
      term: "Breakout Session",
      definition:
        "A smaller meeting that splits off from a larger conference or event, allowing for more focused discussions or specialized topics with smaller groups of attendees.",
      category: "Event Types",
    },
    {
      term: "Block",
      definition:
        "A reserved group of hotel rooms at a negotiated rate, held for event attendees. Rooms are typically held until a cut-off date, after which they're released to general inventory.",
      category: "Venue & Accommodation",
      relatedTerms: ["Cut-off Date", "Attrition Clause", "Pick-up Rate"],
    },

    // C
    {
      term: "Convention & Visitors Bureau",
      acronym: "CVB",
      definition:
        "A local tourism marketing organization that provides information about a destination's attractions, accommodations, meeting facilities, and services. CVBs often assist with event planning and offer destination marketing support.",
      category: "Organizations",
      relatedTerms: ["DMO", "DMC"],
    },
    {
      term: "Catering",
      definition:
        "Food and beverage service for events, including menu planning, dietary accommodations, service style (buffet, plated, family-style), and beverage options including alcohol service.",
      category: "Food & Beverage",
    },
    {
      term: "Cancellation Clause",
      definition:
        "Contract terms that detail the fees, penalties, or procedures if a group cancels their booking entirely. Penalties typically increase as the event date approaches.",
      category: "Contracts & Legal",
      relatedTerms: ["Attrition Clause", "Force Majeure"],
    },
    {
      term: "Cut-off Date",
      definition:
        "The deadline when a hotel releases unused rooms from a group block back to general inventory. After this date, group rates may no longer be available to attendees.",
      category: "Venue & Accommodation",
      relatedTerms: ["Block", "Pick-up Rate"],
    },
    {
      term: "Commission",
      definition:
        "A percentage of the booking value (typically 8-15%) paid by hotels or venues to planners, agencies, or third parties who bring them business. This is the traditional compensation model in hospitality.",
      category: "Payment & Pricing",
    },
    {
      term: "Corporate Event",
      definition:
        "Business-focused gatherings including conferences, meetings, product launches, team building, awards ceremonies, and company celebrations organized by or for corporations.",
      category: "Event Types",
    },

    // D
    {
      term: "Destination Management Company",
      acronym: "DMC",
      definition:
        "A local company specializing in event planning and logistics within a specific destination. DMCs provide comprehensive services including transportation, activities, venue sourcing, entertainment, and on-ground coordination.",
      category: "Organizations",
      relatedTerms: ["CVB", "DMO", "Ground Transportation"],
    },
    {
      term: "Destination Marketing Organization",
      acronym: "DMO",
      definition:
        "An organization focused on promoting a destination to increase tourism and business travel. They support both leisure and group travel, including meetings, conventions, and events.",
      category: "Organizations",
      relatedTerms: ["CVB", "DMC"],
    },
    {
      term: "Dietary Restrictions",
      definition:
        "Food limitations or preferences of attendees including allergies, religious requirements, vegetarian/vegan diets, and other special dietary needs that must be accommodated in catering.",
      category: "Food & Beverage",
    },

    // E
    {
      term: "Event Timeline",
      definition:
        "A detailed schedule of all activities, setup, and breakdown times for an event. Includes vendor arrival times, guest arrival, program schedule, and post-event activities.",
      category: "Event Planning",
      relatedTerms: ["BEO", "Run of Show"],
    },
    {
      term: "Exclusive Use",
      definition:
        "When a venue is reserved entirely for one event, providing complete privacy and control over the space. Often comes at a premium but ensures no competing events or distractions.",
      category: "Venue & Accommodation",
    },

    // F
    {
      term: "Force Majeure",
      definition:
        "A contract clause that frees both parties from liability when extraordinary circumstances beyond their control (natural disasters, pandemics, war, terrorism) prevent contract fulfillment.",
      category: "Contracts & Legal",
      relatedTerms: ["Cancellation Clause", "Insurance"],
    },
    {
      term: "Function Sheet",
      definition:
        "A document detailing the specifics of each event function including room setup, catering requirements, AV needs, and timing. Similar to a BEO but may be less detailed.",
      category: "Event Planning",
      relatedTerms: ["BEO", "Event Timeline"],
    },
    {
      term: "Food & Beverage Minimum",
      acronym: "F&B Minimum",
      definition:
        "The minimum amount that must be spent on catering to secure a venue or receive certain pricing. If not met, additional fees may apply to reach the minimum.",
      category: "Food & Beverage",
    },

    // G
    {
      term: "Group Rate",
      definition:
        "A discounted accommodation rate offered to a block of rooms for event attendees. Typically requires a minimum number of rooms and may include additional perks or amenities.",
      category: "Venue & Accommodation",
      relatedTerms: ["Block", "Net Rate"],
    },
    {
      term: "Ground Transportation",
      definition:
        "Local transportation arrangements including airport transfers, shuttles between venues, motor coaches for group movement, and individual transportation options for attendees.",
      category: "Transportation",
    },
    {
      term: "Green Event",
      definition:
        "An environmentally sustainable event that minimizes waste, uses eco-friendly materials, sources local products, and implements practices to reduce environmental impact.",
      category: "Event Types",
    },

    // H
    {
      term: "Hospitality Suite",
      definition:
        "A private room or space used for entertaining VIPs, sponsors, or special guests. Often includes premium food and beverage service and comfortable seating areas.",
      category: "Venue & Accommodation",
    },
    {
      term: "Hybrid Event",
      definition:
        "An event that combines in-person and virtual attendance, allowing both physical and remote participation through live streaming, interactive technology, and digital engagement tools.",
      category: "Event Types",
    },

    // I
    {
      term: "Incentive Travel",
      definition:
        "Travel programs used as rewards for top-performing employees, partners, or customers. Often includes luxury accommodations, unique experiences, and team-building activities.",
      category: "Event Types",
    },
    {
      term: "Insurance",
      definition:
        "Coverage protecting against event cancellation, liability, property damage, or other unforeseen circumstances. Types include general liability, cancellation, and weather insurance.",
      category: "Contracts & Legal",
    },

    // L
    {
      term: "Load-in/Load-out",
      definition:
        "The process of bringing equipment, supplies, and materials into a venue before an event (load-in) and removing them afterward (load-out). Includes timing, access routes, and equipment requirements.",
      category: "Event Planning",
    },
    {
      term: "Logistics",
      definition:
        "The detailed coordination and implementation of all event components including transportation, accommodations, catering, equipment, staffing, and timeline management.",
      category: "Event Planning",
    },

    // M
    {
      term: "Master Account",
      definition:
        "The primary billing account for a group event that covers charges like meeting room rental, group meals, and other event-related expenses, separate from individual guest rooms.",
      category: "Payment & Pricing",
    },
    {
      term: "Minimum Guarantee",
      definition:
        "The minimum number of attendees or minimum revenue that an event planner guarantees to a venue. Used for pricing calculations and menu planning.",
      category: "Contracts & Legal",
    },

    // N
    {
      term: "Net Rate",
      definition:
        "A non-commissionable rate provided to planners or agencies, representing the base cost before any markups. Gives buyers flexibility to add their own margin or pass savings to clients.",
      category: "Payment & Pricing",
      relatedTerms: ["Commission", "Group Rate"],
    },

    // P
    {
      term: "Pick-up Rate",
      definition:
        "The percentage of reserved room block that is actually booked by attendees. Used to measure the success of room block size and pricing strategy.",
      category: "Venue & Accommodation",
      relatedTerms: ["Block", "Attrition Clause"],
    },
    {
      term: "Plated Service",
      definition:
        "A formal dining style where each guest receives an individually prepared and presented plate of food, typically served by waitstaff. Contrasts with buffet or family-style service.",
      category: "Food & Beverage",
    },

    // R
    {
      term: "Request for Proposal",
      acronym: "RFP",
      definition:
        "A formal document sent to suppliers (hotels, venues, vendors) requesting detailed pricing, availability, and service information for an event. Used to compare options and negotiate contracts.",
      category: "Event Planning",
      relatedTerms: ["Proposal", "Site Inspection"],
    },
    {
      term: "Resell Clause",
      definition:
        "A contract provision allowing hotels to mitigate attrition losses by reselling unused rooms from a group block. Can reduce penalties if rooms are successfully resold.",
      category: "Contracts & Legal",
      relatedTerms: ["Attrition Clause", "Cut-off Date"],
    },
    {
      term: "Run of Show",
      definition:
        "A detailed minute-by-minute timeline of event proceedings including speaker introductions, transitions, technical cues, and all program elements from start to finish.",
      category: "Event Planning",
      relatedTerms: ["Event Timeline", "Production Schedule"],
    },

    // S
    {
      term: "Site Inspection",
      definition:
        "A visit to potential venues to evaluate facilities, services, location, and suitability for an event. Often includes tours, meetings with venue staff, and menu tastings.",
      category: "Event Planning",
      relatedTerms: ["RFP", "Venue Selection"],
    },
    {
      term: "Staging",
      definition:
        "The physical setup and design of event spaces including platforms, backdrops, lighting design, furniture arrangement, and decorative elements to create the desired atmosphere.",
      category: "Event Planning",
    },

    // T
    {
      term: "Travel Management Company",
      acronym: "TMC",
      definition:
        "A company that manages comprehensive business travel services including flight bookings, hotel reservations, car rentals, expense reporting, and travel policy compliance.",
      category: "Organizations",
      relatedTerms: ["Corporate Travel"],
    },
    {
      term: "Turnkey Event",
      definition:
        "A complete event solution where one vendor or company handles all aspects of planning and execution, requiring minimal client involvement beyond initial briefing and approvals.",
      category: "Event Planning",
    },

    // V
    {
      term: "Venue Sourcing",
      definition:
        "The process of identifying, evaluating, and selecting appropriate locations for events based on criteria like capacity, location, budget, amenities, and availability.",
      category: "Venue & Accommodation",
    },
    {
      term: "Virtual Event",
      definition:
        "An online event conducted entirely through digital platforms, allowing remote participation via streaming, video conferencing, and interactive technologies.",
      category: "Event Types",
    },

    // W
    {
      term: "Welcome Reception",
      definition:
        "An opening event designed to greet attendees, facilitate networking, and set the tone for a larger conference or multi-day event. Often includes light refreshments and informal networking.",
      category: "Event Types",
    },
  ];

  // Get unique letters and categories for filters
  const letters = useMemo(() => {
    const uniqueLetters = [...new Set(glossaryEntries.map((entry) => entry.term.charAt(0).toUpperCase()))].sort();
    return ["All", ...uniqueLetters];
  }, []);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(glossaryEntries.map((entry) => entry.category))].sort();
    return ["All", ...uniqueCategories];
  }, []);

  // Filter entries based on search term, letter, and category
  const filteredEntries = useMemo(() => {
    return glossaryEntries
      .filter((entry) => {
        const matchesSearch =
          entry.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (entry.acronym && entry.acronym.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesLetter = selectedLetter === "All" || entry.term.charAt(0).toUpperCase() === selectedLetter;

        const matchesCategory = selectedCategory === "All" || entry.category === selectedCategory;

        return matchesSearch && matchesLetter && matchesCategory;
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [searchTerm, selectedLetter, selectedCategory]);

  // Group entries by first letter for display
  const groupedEntries = useMemo(() => {
    const groups: { [key: string]: GlossaryEntry[] } = {};
    filteredEntries.forEach((entry) => {
      const letter = entry.term.charAt(0).toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  return (
    <div className="w-full min-h-screen bg-landing-primary overflow-x-hidden">
      {/* Hero Section */}
      <section className="container-top">
        <div className="container-page">
          <motion.div
            className="flex flex-col gap-6 max-w-2xl"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-h2 text-landing-primary">Event Planning Glossary</h1>
            <p className="text-body-lg text-landing-secondary">
              Your comprehensive reference guide to event planning, hospitality, and venue management terminology.
              Navigate the industry with confidence using our curated collection of essential terms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content - 2 Column Layout */}
      <section className="bg-landing-primary py-16">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Sidebar - Search and Filters */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-landing-primary shadow-[0_4px_100px_rgba(232,230,241,0.5)] border border-gray-200 p-6 sticky top-6 rounded-3xl lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
                <h2 className="text-xl font-bold text-landing-text-primary mb-6">Filter Glossary</h2>

                {/* Search */}
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-landing-text-secondary w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search terms..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-landing-text-primary placeholder-landing-text-secondary focus:outline-none focus:ring-2 focus:ring-landing-button-primary focus:border-transparent"
                    />
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-landing-text-primary mb-2">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-landing-text-primary focus:outline-none focus:ring-2 focus:ring-landing-button-primary"
                    >
                      {categories.map((category) => (
                        <option key={category} value={category} className="bg-white text-landing-text-primary">
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Letter Filter */}
                  <div>
                    <label className="block text-sm font-medium text-landing-text-primary mb-2">Letter</label>
                    <div className="grid grid-cols-4 gap-1">
                      {letters.map((letter) => (
                        <button
                          key={letter}
                          onClick={() => setSelectedLetter(letter)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                            selectedLetter === letter
                              ? "bg-landing-button-primary text-white font-bold"
                              : "bg-gray-100 text-landing-text-secondary hover:bg-gray-200 hover:text-landing-text-primary"
                          }`}
                        >
                          {letter}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Results Count */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-landing-text-secondary flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      <span className="text-sm">{filteredEntries.length} terms found</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Glossary */}
            <div className="lg:col-span-2">
              {Object.keys(groupedEntries).length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-xl text-landing-text-secondary">No terms found matching your search criteria.</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {Object.entries(groupedEntries)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([letter, entries]) => (
                      <div key={letter} className="space-y-6">
                        <h2 className="text-4xl font-bold text-landing-text-primary border-b border-gray-200 pb-4">
                          {letter}
                        </h2>

                        <div className="grid grid-cols-1 gap-6">
                          {entries.map((entry, index) => (
                            <div
                              key={`${letter}-${index}`}
                              className="bg-landing-primary shadow-[0_4px_100px_rgba(232,230,241,0.5)] border border-gray-200 p-6 rounded-3xl hover:shadow-[0_8px_120px_rgba(232,230,241,0.6)] transition-all duration-300"
                            >
                              <div className="space-y-3">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <h3 className="text-xl font-bold text-landing-text-primary">
                                    {entry.term}
                                    {entry.acronym && (
                                      <span className="ml-3 text-base font-medium text-landing-button-primary bg-blue-50 px-2 py-1 rounded-lg">
                                        {entry.acronym}
                                      </span>
                                    )}
                                  </h3>
                                  <span className="text-sm text-landing-text-secondary bg-gray-100 px-2 py-1 rounded-lg">
                                    {entry.category}
                                  </span>
                                </div>

                                <p className="text-landing-text-secondary leading-relaxed">{entry.definition}</p>

                                {entry.relatedTerms && entry.relatedTerms.length > 0 && (
                                  <div className="pt-2 border-t border-gray-200">
                                    <p className="text-sm text-landing-text-secondary">
                                      <span className="font-medium">Related terms:</span>{" "}
                                      {entry.relatedTerms.join(", ")}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
